// src/components/MainNavbar.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../firebase/useAuth';
import { auth } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import clsx from 'clsx';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { OptimizedImage } from '../utils/imageManager';
import {
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import ThemeToggleButton from './common/ThemeToggleButton';
import { useModal } from '../context/ModalContext';
import SearchModal from './modals/SearchModal';
import EmailVerificationBanner from './common/EmailVerificationBanner';
import { useRank } from '../hooks/useRank';
import { useForecast } from '../contexts/ForecastProvider';

// Custom hook to detect clicks outside an element (unchanged)
function useOutsideAlerter(ref, callback) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback]);
}

const getButtonClasses = ({ variant = 'ghost', isActive = false, className }) => {
  const baseClasses = `
    relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300
    inline-flex items-center group overflow-hidden border border-transparent
  `;

  const variants = {
    primary: `
      bg-primary text-white shadow-lg
      before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/20 before:to-transparent
      before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300
    `,
    ghost: `
      text-content-primary
      bg-black/[.1] dark:bg-white/[.1]
      hover:text-primary
      hover:bg-black/[.15] dark:hover:bg-white/[.15]
      hover:shadow-md
    `,
  };

  const finalVariant = isActive ? 'primary' : variant;

  return clsx(baseClasses, variants[finalVariant], className);
};

function Button({ to, isExternal = false, onClick, children, className, variant = 'ghost', isActive: activeProp, ...props }) {
  const location = useLocation();
  const isActive = activeProp || (!isExternal && to && location.pathname === to);

  const classes = getButtonClasses({
    variant,
    isActive,
    className,
  });

  const content = (
    <>
      {children}
      {isExternal && (
        <ArrowTopRightOnSquareIcon className="ml-1.5 h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
      )}
    </>
  );

  if (isExternal) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={classes} {...props}>
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={classes} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={classes} {...props}>
      {content}
    </button>
  );
}

function Dropdown({ isOpen, children, origin = 'origin-top-left', align = 'left-0', width = 'w-56', className = '' }) {
  return (
    <div
      style={{ WebkitBackdropFilter: 'blur(16px)' }}
      className={clsx(
        'absolute mt-2 rounded-2xl shadow-2xl',
        'bg-[rgba(255,255,255,0.85)] dark:bg-[rgba(39,39,42,0.85)]',
        'backdrop-filter backdrop-blur-lg',
        'border border-border ring-1 ring-black/5 dark:ring-white/10',
        'focus:outline-none transform transition-all duration-300 ease-out',
        isOpen ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none',
        origin,
        align,
        width,
        className
      )}
    >
      <div className="absolute inset-0 rounded-2xl" />
      <div className="relative p-2">{children}</div>
    </div>
  );
}

function MainNavbar() {
  const { userId, userLoading, emailVerified } = useAuth();
  const { triggerRefresh } = useForecast();
  const navigate = useNavigate();

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCourseOpen, setCourseOpen] = useState(false);
  const [isProgressOpen, setProgressOpen] = useState(false);
  const [isProfileOpen, setProfileOpen] = useState(false);
  const { showModal, hideModal } = useModal();

  const courseDropdownRef = useRef(null);
  const progressDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  useOutsideAlerter(courseDropdownRef, () => setCourseOpen(false));
  useOutsideAlerter(progressDropdownRef, () => setProgressOpen(false));
  useOutsideAlerter(profileDropdownRef, () => setProfileOpen(false));

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    setCourseOpen(false);
    setProgressOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  const isCourseActive = location.pathname.startsWith('/course/');
  const isProgressActive = location.pathname.startsWith('/user/words') || location.pathname.startsWith('/user/sentences');
  const isSettingsActive = location.pathname.startsWith('/user/settings') || location.pathname.startsWith('/user/subscription');

  const rank = useRank();

  const handleOpenSearchModal = () => {
    showModal(<SearchModal handleClose={hideModal} />);
  };

  const renderAuthSection = () => {
    if (userLoading) {
      // START: Added wrapper div to match final component structure
      return (
        <div className="relative">
          <Skeleton circle width={40} height={40} />
        </div>
      );
      // END: Added wrapper div
    }

    if (userId) {
      return (
        <div className="relative" ref={profileDropdownRef}>
          <button
            onClick={() => setProfileOpen(!isProfileOpen)}
            className={clsx(
              'relative flex text-sm rounded-full focus:outline-none transition-all duration-300 hover:shadow-lg',
              isSettingsActive ? 'ring-primary shadow-lg' : 'ring-transparent'
            )}
          >
            <span className="sr-only">Open user menu</span>
            <div
              className="relative p-[3px] rounded-full"
              style={rank ? { background: rank.tierBg } : undefined}
            >
              <OptimizedImage
                imageKey="PROFILE"
                className="h-10 w-10 rounded-full block"
                fallback={<div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">U</div>}
              />
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
              {rank && (
                <div
                  className="absolute bottom-0 right-0 text-[9px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full text-white px-0.5 z-10 ring-2 ring-background-secondary"
                  style={{ background: rank.tierBg }}
                >
                  {rank.level}
                </div>
              )}
            </div>
          </button>

          <Dropdown isOpen={isProfileOpen} origin="origin-top-right" align="right-0">
            <div className="space-y-1">
              <Button to="/user/settings" className="block w-full text-left">
                <Cog6ToothIcon className="h-5 w-5 mr-3" />
                Settings
              </Button>
              <Button to="/user/subscription" className="block w-full text-left">
                <CreditCardIcon className="h-5 w-5 mr-3" />
                Subscription
              </Button>
              <div className="!my-2 border-t border-border"></div>
              <Button onClick={handleLogout} className="block w-full text-left">
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                Logout
              </Button>
            </div>
          </Dropdown>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-3">
        <Button to="/user/login" variant="ghost">
          Login
        </Button>
        <Button to="/user/register" variant="primary">
          Register
        </Button>
      </div>
    );
  };

  return (
    <SkeletonTheme baseColor="var(--color-skeleton-base)" highlightColor="var(--color-skeleton-highlight)">
      <>
        <nav className="bg-background-secondary/95 backdrop-blur-lg shadow-lg border-b border-border sticky top-0 z-50 relative">
          <div className="absolute inset-0 bg-background-secondary/90" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo and Desktop Nav */}
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {/* START: Added skeleton for the logo to prevent shift */}
                  {userLoading ? (
                    <div className="flex items-center space-x-3">
                      <Skeleton circle width={32} height={32} />
                      <Skeleton width={100} height={24} style={{ borderRadius: '6px' }} />
                    </div>
                  ) : (
                    <Link to={userId ? '/dashboard' : '/'} onClick={triggerRefresh} className="flex items-center space-x-3 group">
                      <div className="relative">
                        <OptimizedImage 
                          imageKey="LOGO" 
                          className="h-8 w-auto transition-transform duration-300 group-hover:scale-110" 
                        />
                      </div>
                      <span className="text-content-primary font-bold text-xl group-hover:text-primary transition-colors duration-300">Kikikata</span>
                    </Link>
                  )}
                  {/* END: Added skeleton for the logo */}
                </div>

                <div className="hidden lg:flex lg:items-center lg:ml-8 lg:space-x-3">
                  {userLoading ? (
                    <>
                      <Skeleton width={101} height={38} style={{ borderRadius: '12px' }} />
                      <Skeleton width={98} height={38} style={{ borderRadius: '12px' }} />
                      <Skeleton width={109} height={38} style={{ borderRadius: '12px' }} />
                    </>
                  ) : (
                    <>
                      <Button to={userId ? '/dashboard' : '/'}>{userId ? 'Dashboard' : 'Home'}</Button>

                      <div className="relative" ref={courseDropdownRef}>
                        <Button onClick={() => setCourseOpen(!isCourseOpen)} isActive={isCourseActive}>
                          <span>Course</span>
                          <ChevronDownIcon className={clsx('ml-1 h-4 w-4 transition-transform duration-300', isCourseOpen && 'rotate-180')} />
                        </Button>
                        <Dropdown isOpen={isCourseOpen} width="w-48">
                          <div className="space-y-1">
                            <Button to="/course/words" className="block w-full text-left">
                              Words
                            </Button>
                            <Button to="/course/sentences" className="block w-full text-left">
                              Sentences
                            </Button>
                          </div>
                        </Dropdown>
                      </div>

                      {userId && (
                        <div className="relative" ref={progressDropdownRef}>
                          <Button onClick={() => setProgressOpen(!isProgressOpen)} isActive={isProgressActive}>
                            <span>Progress</span>
                            <ChevronDownIcon className={clsx('ml-1 h-4 w-4 transition-transform duration-300', isProgressOpen && 'rotate-180')} />
                          </Button>
                          <Dropdown isOpen={isProgressOpen} width="w-48">
                            <div className="space-y-1">
                              <Button to="/user/words" className="block w-full text-left">
                                My Words
                              </Button>
                              <Button to="/user/sentences" className="block w-full text-left">
                                My Sentences
                              </Button>
                            </div>
                          </Dropdown>
                        </div>
                      )}

                      {!userId && <Button to="/pricing">Pricing</Button>}
                    </>
                  )}
                </div>
              </div>

              {/* Right Section */}
              <div className="hidden lg:flex items-center space-x-3">
                <Button onClick={handleOpenSearchModal}>
                  <MagnifyingGlassIcon className="h-5 w-5 mr-2 relative" />
                  <span className="relative">Search</span>
                </Button>
                <Button to="https://kikikata-docs.web.app/" isExternal>
                  Docs
                </Button>
                {userLoading ? (
                  <Skeleton width={130} height={38} style={{ borderRadius: '12px' }} />
                ) : (
                  userId && (
                    <Button to="https://kikikata.flarum.cloud/" isExternal>
                      Community
                    </Button>
                  )
                )}
                <ThemeToggleButton />
                {renderAuthSection()}
              </div>

              {/* Mobile Menu Button */}
              <div className="flex items-center lg:hidden space-x-2">
                <button
                  onClick={handleOpenSearchModal}
                  className="p-2 rounded-xl text-content-primary hover:text-primary hover:bg-black/[.15] dark:hover:bg-white/[.15] transition-all duration-300"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
                <ThemeToggleButton />
                <button
                  onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
                  type="button"
                  className="bg-black/[.07] dark:bg-white/[.07] inline-flex items-center justify-center p-2 rounded-xl text-content-primary hover:text-primary hover:bg-black/[.15] dark:hover:bg-white/[.15] focus:outline-none transition-all duration-300 border border-transparent"
                >
                  <span className="sr-only">Open main menu</span>
                  <div className="relative w-6 h-6">
                    <div
                      className={clsx(
                        'absolute inset-0 transition-all duration-300',
                        isMobileMenuOpen ? 'rotate-45 opacity-0' : 'rotate-0 opacity-100'
                      )}
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </div>
                    <div
                      className={clsx(
                        'absolute inset-0 transition-all duration-300',
                        isMobileMenuOpen ? 'rotate-0 opacity-100' : 'rotate-45 opacity-0'
                      )}
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu Panel */}
          <div
            className={clsx(
              'lg:hidden transition-all duration-300 ease-in-out overflow-hidden',
              isMobileMenuOpen ? 'max-h-screen opacity-100 pointer-events-auto' : 'max-h-0 opacity-0 pointer-events-none'
            )}
          >
            <div className="bg-background-secondary/95 backdrop-blur-lg border-t border-border">
              <div className="absolute inset-0 bg-background-secondary/90" />
              <div className="relative">
                {userLoading ? (
                  <div className="px-4 py-4 space-y-4">
                    <div>
                      <Skeleton width="35%" height={12} style={{ marginBottom: '8px' }} />
                      <div className="flex space-x-2">
                        <Skeleton width={96} height={38} style={{ borderRadius: '12px' }} />
                        <Skeleton width={110} height={38} style={{ borderRadius: '12px' }} />
                      </div>
                    </div>
                    <div>
                      <Skeleton width="35%" height={12} style={{ marginBottom: '8px' }} />
                      <div className="flex space-x-2">
                        <Skeleton width={96} height={38} style={{ borderRadius: '12px' }} />
                        <Skeleton width={110} height={38} style={{ borderRadius: '12px' }} />
                      </div>
                    </div>
                    <div>
                      <Skeleton width="35%" height={12} style={{ marginBottom: '8px' }} />
                      <div className="space-y-2">
                        <div className="flex space-x-2">
                          <Skeleton width={120} height={38} style={{ borderRadius: '12px' }} />
                          <Skeleton width={120} height={38} style={{ borderRadius: '12px' }} />
                        </div>
                        <div className="flex">
                          <Skeleton width={96} height={38} style={{ borderRadius: '12px' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-4 space-y-4">
                    {!userId && (
                      <div>
                        <div className="flex space-x-2">
                          <Button to="/" className="text-left text-sm">
                            Home
                          </Button>
                          <Button to="/pricing" className="text-left text-sm">
                            Pricing
                          </Button>
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs font-semibold text-content-secondary uppercase tracking-wide mb-2">Course</div>
                      <div className="flex space-x-2">
                        <Button to="/course/words" className="text-left text-sm">
                          Words
                        </Button>
                        <Button to="/course/sentences" className="text-left text-sm">
                          Sentences
                        </Button>
                      </div>
                    </div>
                    {userId && (
                      <div>
                        <div className="text-xs font-semibold text-content-secondary uppercase tracking-wide mb-2">Progress</div>
                        <div className="flex space-x-2">
                          <Button to="/user/words" className="text-left text-sm">
                            My Words
                          </Button>
                          <Button to="/user/sentences" className="text-left text-sm">
                            My Sentences
                          </Button>
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs font-semibold text-content-secondary uppercase tracking-wide mb-2">Resources</div>
                      <div className="flex space-x-2">
                        <Button to="https://kikikata-docs.web.app/" className="text-left text-sm" isExternal>
                          Docs
                        </Button>
                        {userId && (
                          <Button to="https://kikikata.flarum.cloud/" className="text-left text-sm" isExternal>
                            Community
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      {userId ? (
                        <>
                          <div className="text-xs font-semibold text-content-secondary uppercase tracking-wide mb-2">Account</div>
                          <div className="space-y-2">
                            <div className="flex space-x-2">
                              <Button to="/user/settings" className="text-left text-sm">
                                <Cog6ToothIcon className="h-4 w-4 mr-2" />
                                Settings
                              </Button>
                              <Button to="/user/subscription" className="text-left text-sm">
                                <CreditCardIcon className="h-4 w-4 mr-2" />
                                Subscription
                              </Button>
                            </div>
                            <div className="flex">
                              <Button onClick={handleLogout} className="text-left text-sm">
                                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                                Logout
                              </Button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-xs font-semibold text-content-secondary uppercase tracking-wide mb-2">Get Started</div>
                          <div className="flex space-x-2">
                            <Button to="/user/login" variant="ghost" className="flex-1 justify-center">
                              Login
                            </Button>
                            <Button to="/user/register" variant="primary" className="flex-1 justify-center">
                              Register
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
        {userId && !emailVerified && <EmailVerificationBanner />}
      </>
    </SkeletonTheme>
  );
}

export default MainNavbar;
